import { computed, inject, Injectable, signal } from '@angular/core';
import { ListingPageModelAbstract } from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import {
  ListingDataSource,
  ListingPageModel
} from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { ListingTableComponent } from '../../../../shared/components/listing-table/listing-table.component';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { TranslateRegistry } from '../../../../services/translate/translate.registry';
import { OrderDirection } from '../../devices/components/devices.models';
import { MissingProgrammingRecordsListingFiltersSourceService } from './missing-programming-records-listing-filters-source.service';
import {
  MISSING_PROGRAMMING_RECORDS_DEFAULT_SEARCH_TYPE,
  MISSING_PROGRAMMING_RECORDS_SEARCH_TYPES,
  MISSING_PROGRAMMING_RECORDS_SORT_FIELD
} from './missing-programming-records-listing-constants';
import { MissingProgrammingRecordsFieldName, MissingProgrammingRecordsList } from './missing-programming-records.types';
import { ExportRow } from '../../../../shared/types/export.types';
import { TableData } from '../../../../shared/types/api-response.types';
import { TimezoneService } from '../../../../services/utility/timezone.service';
import { MissingProgrammingRecordsService } from './missing-programming-records.service';

@Injectable()
export class MissingProgrammingRecordsListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  private readonly missingProgrammingRecordsService = inject(MissingProgrammingRecordsService);
  private readonly timezoneService = inject(TimezoneService);

  readonly pageId = ListingPageId.MissingProgrammingRecords;
  readonly pageTitle = TranslateRegistry.get.MAIN_MENU.TITLE.MISSING_PROGRAMMING_RECORDS;

  readonly searchTypeList = computed(() => MISSING_PROGRAMMING_RECORDS_SEARCH_TYPES);
  readonly selectedSearchType = computed(() => MISSING_PROGRAMMING_RECORDS_DEFAULT_SEARCH_TYPE);

  readonly sortField = MISSING_PROGRAMMING_RECORDS_SORT_FIELD;
  readonly sorting = computed(() => ({
    columnName: MissingProgrammingRecordsFieldName.LastTransmissionTime,
    orderDirection: OrderDirection.DESC
  }));

  readonly tableColumns = signal([
    {
      field: 'commDeviceId',
      title: TranslateRegistry.get.INSTALLATION_HISTORY.TABLE.COMM_DEVICE_ID,
      width: 200,
      sortable: true
    },
    {
      field: 'initialTransmissionTime',
      title: TranslateRegistry.get.FILTER.TITLE.INITIAL_TRANSMISSION_TIME,
      width: 220,
      sortable: true
    },
    {
      field: 'lastTransmissionTime',
      title: TranslateRegistry.get.FILTER.TITLE.LAST_TRANSMISSION_TIME,
      width: 220,
      sortable: true
    }
  ]);

  readonly dataSource: ListingDataSource<MissingProgrammingRecordsList> = {
    getAll: tableState =>
      this.missingProgrammingRecordsService.readList({
        ...tableState,
        filters: (tableState.filters ?? []).map(f => ({
          ...f,
          filteredValues:
            f.filterType === MissingProgrammingRecordsFieldName.DateTimeUTC && f.filteredValues.length === 2
              ? this.timezoneService.getShiftedDatepickerRangeStringified({
                  start: new Date(f.filteredValues[0]),
                  end: new Date(f.filteredValues[1])
                })
              : f.filteredValues
        }))
      }),
    total: 0
  };

  readonly exportConfig = {
    fileName: 'Export-MissingProgrammingRecords',
    mapData: (data: TableData<unknown>): ExportRow[] => {
      return data.data.map(item => {
        const row = item as Record<string, unknown>;
        return {
          commDeviceId: { title: 'CommDeviceID', value: row['commDeviceId'] },
          initialTransmissionTime: { title: 'InitialTransmissionTime', value: row['initialTransmissionTime'] },
          lastTransmissionTime: { title: 'LastTransmissionTime', value: row['lastTransmissionTime'] }
        };
      });
    }
  };

  readonly exportButtonVisible = true;
  readonly columnsButtonVisible = false;
  readonly resetButtonVisible = false;
  readonly tableCmp = ListingTableComponent;
  readonly filterSource = inject(MissingProgrammingRecordsListingFiltersSourceService);

  constructor() {
    super();
    this.listingPageModelRegistry.add(this.pageId, this);
  }
}
